import { createAction } from '@reduxjs/toolkit';
import { Location, Action } from 'history';
import { CMxComponents } from '@codametrix/ui-components/src/types';
import { commonActions } from '@codametrix/ui-common';
import { Loading, UserConfig } from './action-types';
import {
  ANALYTICS_ACTION_METADATA,
  BROADCAST_ACTION_METADATA
} from './action-constants';

/**
 * Leaf module for primitive UI action creators.
 *
 * These actions are intentionally kept separate from UI thunks and navigation
 * helpers so reducers and lower-level action modules can depend on shared UI
 * action types without importing the larger `actions/ui` compatibility barrel.
 */
type LocationChangePayload = { location: Location; action: Action };

const { CMxApplication, ParentWindow, Router, UserInterface } = commonActions;

/**
 * Logs the user out of the dashboard shell.
 *
 * Logout is tracked for analytics and broadcast to peer windows so child or
 * parent dashboard contexts can clean up together.
 */
const logout = createAction(UserInterface.LOGOUT, () => ({
  payload: undefined as void,
  meta: { ...ANALYTICS_ACTION_METADATA, ...BROADCAST_ACTION_METADATA }
}));

/**
 * Replaces the admin navigation tabs shown by the dashboard shell.
 */
const changeAdminTab = createAction<CMxComponents.TabDef[]>(
  UserInterface.ADMIN_TAB_CHANGE
);

/**
 * Mirrors history location changes into Redux state.
 */
const onLocationChange = createAction<LocationChangePayload>(
  Router.LOCATION_CHANGE
);

/**
 * Records a user-initiated dashboard navigation selection.
 */
const navigate = createAction(
  UserInterface.NAVIGATE,
  (payload: CMxAPI.NavAction | CMxComponents.TabDef) => ({
    payload,
    meta: { user: true }
  })
);

/**
 * Tracks whether a parent window has navigated while this window is active.
 */
const parentDidNavigate = createAction<boolean>(
  ParentWindow.PARENT_NAVIGATION
);

/**
 * Stores user configuration once it has been fetched during bootstrap.
 */
const configAvailable = createAction<CMxAPI.UserConfig>(
  UserConfig.CONFIG_AVAILABLE
);

/**
 * Sets the application-level loading flag used by the dashboard shell.
 */
const loading = createAction<boolean>(CMxApplication.LOADING);

/**
 * Rehydrates the dashboard UI slice from an external interface state payload.
 */
const replaceUIState = createAction<CMx.InterfaceState>(
  UserInterface.REPLACE_UI_STATE
);

/**
 * Sets structured loading state for named UI workflows.
 */
const loadingAction = createAction<CMx.LoadingPayload>(Loading.LOADING_ACTION);

/**
 * Stores the active user in UI state and records the update for analytics.
 */
const setActiveUser = createAction(
  UserInterface.SET_ACTIVE_USER,
  (payload: CMxAPI.User) => ({
    payload,
    meta: { ...ANALYTICS_ACTION_METADATA }
  })
);

/**
 * Updates the PHI masking state used by case detail and shortcut flows.
 */
const detailTogglePHI = createAction<CMx.PHIMask>(
  UserInterface.DETAIL_TOGGLE_PHI
);

/**
 * Saves the user's default homepage preference in UI state.
 */
const setHomepagePreference = createAction<CMxAPI.Preference | undefined>(
  UserInterface.SET_DEFAULT_HOMEPAGE
);

/**
 * Stores the path used by case detail flows to return to the worklist.
 */
const setBackToWorklistPath = createAction<{ path: string }>(
  UserInterface.SET_BACK_TO_WORKLIST_PATH
);

/**
 * Stores the patient information layout configuration used by case detail.
 */
const setPropertyConfig = createAction<
  CMxCommonApp.PatientInformationConfiguration
>('SET_PROPERTY_CONFIG');

/**
 * Requests that the window-open middleware launch a managed child window.
 */
const openWindow = createAction<CMxCommonApp.WindowOpenArguments>(
  UserInterface.OPEN_CHILD_WINDOW
);

/**
 * Opens or closes the main dashboard drawer.
 */
const toggleDrawer = createAction<boolean>(CMxApplication.DRAWER);

/**
 * Opens or closes the drawer's content region independently of the drawer shell.
 */
const toggleDrawerContent = createAction<boolean>(
  CMxApplication.DRAWER_CONTENT
);

export {
  changeAdminTab,
  configAvailable,
  detailTogglePHI,
  loading,
  loadingAction,
  logout,
  navigate,
  onLocationChange,
  openWindow,
  parentDidNavigate,
  replaceUIState,
  setActiveUser,
  setBackToWorklistPath,
  setHomepagePreference,
  setPropertyConfig,
  toggleDrawer,
  toggleDrawerContent
};
